import { Fragment, useCallback, useEffect, useMemo, useState, type FC } from 'react'
import styled from 'styled-components'
import { MODALS, TypeActiveFilter } from '@/context'
import { useFilters, useModal, useTranslator } from '@/hooks'
import type { TypeCmsProductsByCategoryList, TypeLang } from '@/schema'
import { designGrid, getP18, getP20_1, mq, rgbAlpha, vw } from '@/styles'
import { ModalFilters } from './modals/ModalFilters'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { SliderFlickity } from './commons'
import { AnimatePresence, motion } from 'motion/react'

type Props = {
  data: TypeCmsProductsByCategoryList['filters']
}

const getFieldsHighlighted = (data: Props['data'], activeFilters, disabledFilters, lang: TypeLang, withSvg?: boolean) => data.map((item, _idx) => {
  const paramKeys = Object.keys(item.values)
  // const disabledKeyObj = disabledFilters?.[item?.slug?.[lang]]
  // const disabledCounter = disabledKeyObj ? Object.keys(disabledKeyObj)?.length : 0
  // const paramsCounter = paramKeys?.length
  // const allDisabled = disabledCounter > 0 && disabledCounter === paramsCounter

  return (
    <Fragment key={`${_idx}-${item.id}`}>
      {paramKeys?.map((paramValue, index) => {
        const isChecked = activeFilters?.[item.slug[lang]]?.[paramValue] ?? false
        const isDisabled = !isChecked && (Object.keys(activeFilters)?.length > 1 || !activeFilters?.[item.slug[lang]]) && disabledFilters?.[item.slug[lang]]?.[paramValue]

        const id = `${item.slug[lang]}__${paramValue + ''}`

        return <label htmlFor={id} className='filter-highlighted' key={`${index}-${paramValue}`} data-checked={isChecked} data-disabled={isDisabled}>
					{/* eslint-disable-next-line @next/next/no-img-element */}
					{withSvg && item.values[paramValue]?.svg && <img src={item.values[paramValue].svg.url}  alt={item.values[paramValue].svg.alt} width={16} height={16}/>}
					{/* eslint-disable-next-line @next/next/no-img-element */}
					{/* {withSvg && <img src={`/images/svg/i--plumilles-0${index === 1 || index === 2 || index === 3 ? index : 1}.svg`} alt={item.values[paramValue].name} width={16} height={16}/>} */}
					<span>{item.values[paramValue].name}</span>
				</label>
      })}
    </Fragment>
  )
})

const getActiveFilterMirrorButtons = (activeFilters:TypeActiveFilter) => {
  if(Object.keys(activeFilters)?.length === 0) return []
  const filterKeys = Object.keys(activeFilters)
  const buttons = []
  for(const filterKey of filterKeys) {

    const filterObj = activeFilters[filterKey]
    const values = Object.keys(filterObj)
    values.forEach((value) => {
      const id = `${filterKey}__${value}`

      buttons.push(<label key={id} htmlFor={id}>
        <span>{value + ''} &#215;</span>
      </label>)
    })
  }

  return buttons
}


const checkIfHasMoreThanOneInstallation = (activeFilters: TypeActiveFilter, disabledFilters, highlightedTypologyFilters: TypeCmsProductsByCategoryList['filters'], highlightedInstallationFilters: TypeCmsProductsByCategoryList['filters'], lang: TypeLang) => {
	// If there is no active filter, return an empty array
  const someActiveFilters = Object.keys(activeFilters?.[highlightedTypologyFilters?.[0]?.slug?.[lang]] || {})?.length > 0
	if(!someActiveFilters) return []

	// Some categories (e.g. led-strip) have no installation filter group, so bail out early
	if(!highlightedInstallationFilters?.[0]?.values) return []

	const paramKeyname = highlightedInstallationFilters?.[0]?.slug?.[lang]

	// Take out the installationValues that are disabled
	// Ignores from the disabledFilters the values that are present in the activeFilters
	// Returns the array of the remaining installation values (which can contain the valid values and the ones present in the activeFilters)

	const disabledInstallationValues = Object.keys(disabledFilters?.[paramKeyname] || {})
	// example: {carril: true, sobre-mesa: true, suelo: true}

	const activeInstallationValues = Object.keys(activeFilters?.[paramKeyname] || {})
	// example: {suelo: true}

	// Take out the installationValues that are disabled but not present in the activeFilters
	const allTheDisabledValues = disabledInstallationValues.filter(value => !activeInstallationValues.includes(value))

	const remainingInstallationValues = Object.keys(highlightedInstallationFilters?.[0]?.values || {}).filter(item => !allTheDisabledValues.includes(item))

	return remainingInstallationValues
}

export const SectionFilters:FC<Props> = ({ data }) => {
  const { toggle, updatePayload } = useModal(MODALS.FILTERS)
  const { t } = useTranslator()
  const pathname = usePathname()
	const searchParams = useSearchParams()
  const { replace } = useRouter()
	const { lang }: { lang: TypeLang } = useParams()
  const [forceResetFilters, setForceResetFilters] = useState(false)
  const [fieldGroupToReset, setFieldGroupToReset] = useState<string | null>(null)
  const { activeFilters, disabledFilters, setActiveFilters } = useFilters()

  const Items = useMemo(() => getActiveFilterMirrorButtons(activeFilters), [activeFilters])
	const highlightedTypologyFilters = useMemo(() => data.filter(item => item.sage_id === 'Installation'), [data])
	const typologiesSlug = useMemo(() => data.filter(item => item.sage_id === 'Installation')[0]?.slug?.[lang], [data, lang])
	const installationSlug = useMemo(() => data.filter(item => item.sage_id === 'CodigoSistemaInsES')[0]?.slug?.[lang], [data, lang])
	const highlightedInstallationFilters = useMemo(() => data.filter(item => item.sage_id === 'CodigoSistemaInsES'), [data])

	const activeTypologiesHasMoreThanOneInstallation = useMemo(() => checkIfHasMoreThanOneInstallation(activeFilters, disabledFilters, highlightedTypologyFilters, highlightedInstallationFilters, lang),[activeFilters, disabledFilters, highlightedTypologyFilters, highlightedInstallationFilters, lang])

	const updateUrl = useCallback((activeFilters) => {
    const q = searchParams.get('q')
    const params = new URLSearchParams()

    if(q?.length > 0) params.set('q', q)


    Object.keys(activeFilters).forEach((key) => {
      const values = Object.keys(activeFilters[key])?.join(',')
      params.set(key, values)
    })

    const newUrl = `${pathname}?${params.toString()}`

    replace(newUrl)
    window.history.pushState({ path: newUrl }, '', newUrl)

  }, [pathname, searchParams, replace])

  const onClickClear = () => {
    setForceResetFilters(true)
  }

	const onClickClearTypologiesFilters = useCallback(() => {
		let urlActiveFilters = {}
		setActiveFilters(prev => {
			const incomingActiveFilters = {...prev}
			delete incomingActiveFilters[typologiesSlug]

			urlActiveFilters = incomingActiveFilters

			return incomingActiveFilters
		})

		updateUrl(urlActiveFilters)
		setFieldGroupToReset(typologiesSlug)
	}, [typologiesSlug, setActiveFilters, updateUrl])

	const onClickClearInstallationsFilters = useCallback(() => {
		let urlActiveFilters = {}
		setActiveFilters(prev => {
			const incomingActiveFilters = {...prev}
			delete incomingActiveFilters[installationSlug]

			urlActiveFilters = incomingActiveFilters

			return incomingActiveFilters
		})

		updateUrl(urlActiveFilters)
		setFieldGroupToReset(installationSlug)
	}, [installationSlug, setActiveFilters, updateUrl])


	console.log('🔵 activeFilters', activeFilters)
	console.log('🟡 disabledFilters', disabledFilters)
	console.log('🟡 typologiesSlug', typologiesSlug)

  useEffect(() => {
    updatePayload({ close: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <Section className='filters-bar'>
      <div>
				<div className='row no-flex with-svgs'>
					<SliderFlickity options={{ cellAlign: 'center', draggable: true }} reloadOnUpdate={false} dynamicSlider={true}>
						<button type='button' className='btn--clear-typologies filter-highlighted' data-active={!activeFilters?.[typologiesSlug]} onClick={onClickClearTypologiesFilters}>
							<span>{t('project.filters.all_typologies')}</span>
						</button>
						{getFieldsHighlighted(highlightedTypologyFilters, activeFilters, disabledFilters, lang, true)}
					</SliderFlickity>
				</div>
				<AnimatePresence mode='wait'>
					{activeTypologiesHasMoreThanOneInstallation?.length > 1 && (
						<motion.div key={activeTypologiesHasMoreThanOneInstallation?.length} className='row no-flex just-text' initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: '40px' }}  exit={{ opacity: 0, height: 0 }} data-has-more={activeTypologiesHasMoreThanOneInstallation}>
							<SliderFlickity options={{ cellAlign: 'center', draggable: true }} reloadOnUpdate={false} dynamicSlider={true}>
								<button type='button' className='btn--clear-installations filter-highlighted' data-active={!activeFilters?.[installationSlug]} onClick={onClickClearInstallationsFilters}>
									<span>{t('project.filters.all_installations')}</span>
								</button>
								{getFieldsHighlighted(data.filter(item => item.sage_id === 'CodigoSistemaInsES'), activeFilters, disabledFilters, lang, false)}
							</SliderFlickity>
						</motion.div>
					)}
				</AnimatePresence>
        <div className='row'>
          <button className='btn--trigger' onClick={toggle}>
            <svg aria-label='Plus icon' height='40' role='img' viewBox='0 0 40 40' width='40' xmlns='http://www.w3.org/2000/svg'>
              <path d='M20 10v20M10 20h20' fill='none' stroke='currentColor' strokeLinecap='round' strokeWidth='2'/>
            </svg>
            <span>{t('project.filters.more_filters')}</span>
          </button>
          <div className='active-filters'>
            {Items?.length > 0 &&
              <SliderFlickity options={{ cellAlign: 'left', draggable: Items?.length > 4 }} reloadOnUpdate={true} dynamicSlider={true}>{Items}</SliderFlickity>}
          </div>
          {/* <button onClick={toggle} {...(!isActive || Object.keys(activeFilters)?.length === 0) && { disabled: true }} className='btn--apply-filters'>
            {t('project.filters.apply_filters')}
          </button> */}
          <button className='btn--clear-filters' {...(Object.keys(activeFilters)?.length === 0) && { disabled: true }} onClick={onClickClear}>{t('project.filters.clear_filters')}</button>
        </div>
        <ModalFilters key={pathname} data={data} forceResetFilters={forceResetFilters} setForceResetFilters={setForceResetFilters} fieldGroupToReset={fieldGroupToReset} setFieldGroupToReset={setFieldGroupToReset} />
      </div>
    </Section>
  )
}

const Section = styled.section`
  background-color: var(--color-background);
  margin-bottom: ${vw(20, 'mobile')};
  padding: 10px 0 5px;
  position: relative;
  top: 0;
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(20, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 20px;
  }

  > div {
    ${designGrid({})}
		overflow-x: clip;
    position: relative;
		max-width: 100vw;
		max-width: 100dvw;
  }

  .active-filters {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    flex-grow: 2;
    gap: 8px;
    justify-content: flex-start;
    overflow: hidden;

    .slider-wrapper {
      width: 100%;
    }

    .flickity-cell {
      padding: 0 8px;

      &:first-child {
        padding-left: 0;
      }

      &:last-child {
        padding-right: 0;
      }
    }

    ${mq.lessThan('tablet')} {
      display: none;
    }

    ${mq.greaterThan('desktop-xs')} {
      gap: 15px;
    }

    label {
      align-items: center;
      cursor: pointer;
      display: flex;
      gap: 2px;
      justify-content: center;
      opacity: .4;
      text-transform: capitalize;
      transition: opacity 250ms ease-in-out;

      svg {
        margin-top: 3px;
        object-fit: contain;
        width: 8px;
      }

      &:hover {
        opacity: 1;
      }
    }
  }

  .row {
    align-items: center;
    display: flex;
    background-color: var(--color-background);
    grid-column: 1 / span 6;
    justify-content: space-between;
    z-index: 3;

		&.no-flex {
			display: block;
			height: 45px;

			&.just-text {
				min-height: 30px;
				margin-bottom: 40px;
				margin-top: 20px;

				.filter-highlighted {
					height: 30px;

					&[data-active='true'] {
						font-weight: 500;
						opacity: 1;
					}
				}

				span {
					height: 30px;
				}
			}

			&.with-svgs {
				height: 100px;
				margin-bottom: 20px;

				.filter-highlighted {
					align-items: center;
					display: flex;
					gap: 0;
					flex-direction: column;
					justify-content: center;
					min-width: 100px;
					padding: 0 10px;
					transition: opacity 250ms ease-in-out;

					&[data-disabled='true'] {
						opacity: .1;
						pointer-events: none;
					}
				}

				.btn--clear-typologies {
					height: 100%;

					&,
					span {
						font-size: 14px;

						${mq.greaterThan('tablet')} {
							font-size: 18px;
						}
					}

					&[data-active='true'] {
						font-weight: 500;
						opacity: 1;
					}
				}

				img {
					height: 80px;
					object-fit: contain;
					width: 80px;
				}

				span {
					font-size: 14px;
					letter-spacing: -.1px;
				}
			}
		}

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }

    button {
      &.btn--trigger {
        align-items: center;
        display: flex;
        ${getP20_1()}
        justify-content: center;
        margin-right: auto;
        padding: 5px 15px;
        position: relative;
        transform: translateX(-15px);
        width: auto;

        svg {
          height: 30px;
          margin-right: 4px;
          width: 30px;
        }
      }

      &.btn--apply-filters,
      &.btn--clear-filters {
        ${getP18()}
        padding: 2px 7px;
        white-space: nowrap;

        ${mq.greaterThan('tablet')} {
          padding: 5px 15px;
        }

        &:disabled,
        &[disabled='true'] {
          opacity: 0;
          pointer-events: none;
        }
      }

      &.btn--apply-filters {
        background-color: rgb(77, 77, 77);
        border-radius: 50px;
        color: var(--color-background);
        margin-left: auto;
        margin-right: 5px;
        opacity: .8;
        position: relative;
        transform: translateX(15px);
        transition: 250ms opacity linear;
        width: auto;

        &:hover {
          background-color: ${rgbAlpha('--color-black', .8)};
        }
      }

      &.btn--clear-filters {
        opacity: .8;
        margin-right: 2px;
        position: relative;
        transform: translateX(15px);
        transition: 250ms opacity linear;
        width: auto;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

	.row__title {
		font-size: 14px;

		${mq.greaterThan('tablet')} {
			font-size: 18px;
		}
	}

	.filter-highlighted {
		align-items: center;
		background-color: var(--color-background);
		cursor: pointer;
		display: flex;
		gap: 2px;
		justify-content: center;
		min-width: 60px;
		opacity: .4;
		padding: 0 5px;
		transition: opacity 250ms ease-in-out;

		&:hover {
			opacity: 1;
		}

		&[data-checked='true'] {
			opacity: 1;

			span {
				font-weight: 600;
			}
		}

		&[data-disabled='true'] {
			display: none;
			pointer-events: none;
			width: 0;
		}

		img {
			height: 35px;
			mix-blend-mode: multiply;
			object-fit: contain;
			width: fit-content;
		}

		span {
			align-items: center;
			display: flex;
			font-size: 14px;
			height: 14px;
			padding: 0 5px;
			text-align: center;
			white-space: nowrap;

			${mq.greaterThan('tablet')} {
				font-size: 18px;
			}
		}
	}
`
